import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import orderServices from "../../services/orders/orderservice";
import OrdersLayer from "./OrdersLayer";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import DriverSummaryPanel from "./DriverSummaryPanel";
import OrdersDialog from "./OrdersDialog";
import { Calendar } from "primereact/calendar";
import { Tooltip } from "primereact/tooltip";
import { DailyMapOrder } from "../../models/orders/daily-map";
import { dailyMapDrivers, dailyMapTotals, distinctDailyOrders, DailyMapFilter, matchesDailyMapFilter } from "../../utils/daily-map";
import { calendarDate, dateForCalendar, indiaToday } from "../../utils/delivery-planning";

export type Order = DailyMapOrder;

function parseGpsLocation(gps?: string | null): { lat: number; lng: number } | null {
  if (!gps || !gps.includes(",")) return null;
  const [a, b] = gps.split(",");
  const lat = parseFloat(a.trim());
  const lng = parseFloat(b.trim());
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

function gpsKey(gps?: string | null): string | null {
  if (!gps) return null;
  return gps.replace(/\s+/g, "").trim();
}

function deduplicateByLocation(list: Order[]): Order[] {
  const byLoc = new Map<string, Order>();
  for (const o of list) {
    const key = gpsKey(o.gpsLocation);
    if (!key) continue;
    const prev = byLoc.get(key);
    if (!prev || (o.sequenceNo ?? 0) > (prev.sequenceNo ?? 0)) {
      byLoc.set(key, o);
    }
  }
  return Array.from(byLoc.values());
}

function groupByLocation(list: Order[]): Record<string, Order[]> {
  const g: Record<string, Order[]> = {};
  for (const o of list) {
    const key = gpsKey(o.gpsLocation);
    if (!key) continue;
    (g[key] ??= []).push(o);
  }
  Object.values(g).forEach(arr => arr.sort((a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0)));
  return g;
}
 
export default function OrdersMap() {
  const [rawOrders, setRawOrders] = useState<Order[]>([]);
  const [totals, setTotals] = useState(() => dailyMapTotals([]));
  const [historical, setHistorical] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(false);
  const loadSequence = useRef(0);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);
  const [selectedLocKey, setSelectedLocKey] = useState<string | null>(null);
  const [highlightLocKeys, setHighlightLocKeys] = useState<string[]>([]);

  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [cancelledOrders, setCancelledOrders] = useState<Order[]>([]);
  const [showCancelledDialog, setShowCancelledDialog] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [mapCenter, setMapCenter] = useState({ lat: 12.3051, lng: 76.6552 });
  const [searchId, setSearchId] = useState<number | "">("");
  const mapRef = useRef<google.maps.Map | null>(null);
  const [searchNumber, setSearchNumber] = useState<string>("");
  const [dialogTitle, setDialogTitle] = useState("Orders");
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => dateForCalendar(indiaToday()));

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyAoYLkna0JudEVB9imblFTHGJwGLYyw0_k",
  });

  const fetchOrders = useCallback(async (date?: string) => {
  const sequence = ++loadSequence.current;
  setLoading(true); setLoadError("");
  try {
    const data = await orderServices.getDriverOrdersDailySummary(date);
    if (sequence !== loadSequence.current) return;
    const list = distinctDailyOrders(data.orders);
    setRawOrders(list);
    setHistorical(!!data.isHistorical);
    setTotals(dailyMapTotals(list));
    setSelectedDriverId(null); setSelectedLocKey(null); setSelectedOrderId(null);
    setHighlightLocKeys([]); setShowCancelledDialog(false); setCancelledOrders([]); setGlobalFilter("");
  } catch {
    if (sequence === loadSequence.current) {
      setRawOrders([]); setTotals(dailyMapTotals([])); setShowCancelledDialog(false);
      setSelectedDriverId(null); setSelectedLocKey(null); setSelectedOrderId(null); setHighlightLocKeys([]);
      setLoadError("Unable to load delivery assignments for this date. Please retry.");
    }
  } finally {
    if (sequence === loadSequence.current) setLoading(false);
  }
}, []);

  const cancelPendingLoads = useCallback(() => { ++loadSequence.current; }, []);
  useEffect(() => {
    void fetchOrders(indiaToday());
    return cancelPendingLoads;
  }, [fetchOrders, cancelPendingLoads]);

  const ICON_SIZE = 55;
  const pinSvgUrl = (color: string, text: number | string) => {
    const fontSize = Math.floor(ICON_SIZE / 7);
    const svg = `
      <svg width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2c-3.9 0-7 3.1-7 7 0 5.3 7 13 7 13s7-7.7 7-13c0-3.9-3.1-7-7-7z"
          fill="${color}" stroke="white" stroke-width="1"/>
        <text x="12" y="13" text-anchor="middle" font-size="${fontSize}" fill="black" font-weight="bold" font-family="sans-serif">${text}</text>
      </svg>`;
    return "data:image/svg+xml;base64," + btoa(svg);
  };

  const handleSearchByNumber = () => {
  if (!searchNumber) return;

  const matchedOrders = rawOrders.filter(o => o.userNumber === searchNumber);
  if (matchedOrders.length === 0) {
    return alert("No orders found for this customer number!");
  }

  const keys: string[] = [];
  matchedOrders.forEach(order => {
    const key = gpsKey(order.gpsLocation);
    if (key) keys.push(key);
    const pos = parseGpsLocation(order.gpsLocation);
    if (pos) {
      mapRef.current?.panTo(pos); 
      mapRef.current?.setZoom(16);
    }
  });

  setSelectedOrderId(matchedOrders[0].orderID); 
  setSelectedLocKey(keys[0] ?? null);          
  setHighlightLocKeys(keys);
  // Animate for 3 seconds, then clear highlights
  window.setTimeout(() => setHighlightLocKeys([]), 3000);
};


  const handleSearchByDate = async () => {
    const dateStr = selectedDate ? calendarDate(selectedDate) : indiaToday();
    await fetchOrders(dateStr);
  };

const uniqueOrders = useMemo(
  () => distinctDailyOrders(rawOrders),
  [rawOrders]
);

const driversSummary = useMemo(() => dailyMapDrivers(uniqueOrders), [uniqueOrders]);
const visibleOrders = useMemo(() => uniqueOrders.filter(o => !selectedDriverId || o.driverID === selectedDriverId), [uniqueOrders, selectedDriverId]);
const groups = useMemo(() => groupByLocation(visibleOrders), [visibleOrders]);
const markers = useMemo(() => deduplicateByLocation(visibleOrders), [visibleOrders]);

  const titleFor = (status: DailyMapFilter) => status === "delivered" ? "Delivered Orders" : status === "pending"
    ? historical ? "Delivery Time Out / Pending Outcomes" : "Yet To Deliver / Delivery Time Out"
    : status === "delayed" ? "Delayed Orders"
    : status === "cancelled" ? "Cancelled Orders"
    : status === "unknown" ? "Historical Outcome Unknown" : "All Assigned Orders";

  const handleTagClick = (status: DailyMapFilter) => {
    setDialogTitle(titleFor(status));
    setCancelledOrders(rawOrders.filter(o => matchesDailyMapFilter(o, status)));
    setShowCancelledDialog(true);
    setGlobalFilter("");
  };

  const handleDriverOrdersClick = (driverID: number, status: DailyMapFilter) => {
    setSelectedDriverId(driverID);
    if (status === "total") return;
    setDialogTitle(titleFor(status));
    setCancelledOrders(rawOrders.filter(o => o.driverID === driverID && matchesDailyMapFilter(o, status)));
    setShowCancelledDialog(true);
    setGlobalFilter("");
  };

  const handleSearchById = () => {
  if (!searchId) return;

  const matchedOrder = rawOrders.find(o => o.orderID === Number(searchId));
  if (!matchedOrder) {
    return alert("Order not found!");
  }

  const pos = parseGpsLocation(matchedOrder.gpsLocation);
  const key = gpsKey(matchedOrder.gpsLocation);
  if (!pos || !key) return alert("Invalid GPS location!");

  setSelectedOrderId(matchedOrder.orderID);
  setSelectedLocKey(key);
  setMapCenter(pos);
  mapRef.current?.panTo(pos);
  mapRef.current?.setZoom(16);

  setHighlightLocKeys([key]);
  window.setTimeout(() => setHighlightLocKeys([]), 3000);
};


  if (!isLoaded) return <div>Loading map…</div>;

  return (
    <div>
      {/* Search Section */}
      <div style={{ margin: 12, display: "flex", gap: 8, alignItems: "center" }}>
        <InputText
  type="number"
  value={searchId !== "" ? searchId.toString() : ""}
  onChange={e => {
    const val = e.target.value ? Number(e.target.value) : "";
    setSearchId(val);
     if (!val && !searchNumber) {
      setSelectedLocKey(null);
      setSelectedOrderId(null);
    }
  }}
  placeholder="Search Order ID"
/>

<InputText
  type="text"
  value={searchNumber}
  maxLength={12}
  onChange={e => {
    const val = e.target.value;
    if (/^[0-9]*$/.test(val)) {
      setSearchNumber(val);
    }

    if (!val && !searchId) {
      setSelectedLocKey(null);
      setSelectedOrderId(null);
    }
  }}
  placeholder="Search Customer Number"
/>


        <Button
          icon="pi pi-search"
          tooltip="Search"
          tooltipOptions={{ position: "top" }}
          onClick={() => {
            if (searchId && searchNumber) {
              const matchedOrder = rawOrders.find(
                o => o.orderID === Number(searchId) && o.userNumber === searchNumber
              );
              if (matchedOrder) {
                const pos = parseGpsLocation(matchedOrder.gpsLocation);
                const key = gpsKey(matchedOrder.gpsLocation);
                if (!pos || !key) return alert("Invalid GPS location!");
                setSelectedLocKey(key);
                setSelectedOrderId(matchedOrder.orderID);
                setMapCenter(pos);
                mapRef.current?.panTo(pos);
                mapRef.current?.setZoom(16);
                setHighlightLocKeys([key]);
                window.setTimeout(() => setHighlightLocKeys([]), 3000);
              } else {
                alert("Order Is Not Found!");
              }
            } else if (searchId) {
              handleSearchById();
            } else if (searchNumber) {
              handleSearchByNumber();
            } else {
              alert("Please enter Order ID or Customer Number to search!");
            }
          }}
        />

               <Calendar
  inputId="daily-map-date"
  aria-label="Assignment date"
  value={selectedDate}
  onChange={(e) => setSelectedDate(e.value as Date)}
  dateFormat="dd/mm/yy"       
  maxDate={dateForCalendar(indiaToday())}
  showIcon
  placeholder="Select Date"
   readOnlyInput
/>
        <Button
          //label="Search by Date"
          tooltip="Search by Date"
          aria-label="Search by Date"
          tooltipOptions={{ position: "top" }}
          icon="pi pi-search"
          onClick={handleSearchByDate}
        />
                <Button
  icon="pi pi-refresh"
  tooltip="Reset All"
  tooltipOptions={{ position: "top" }}
  // onClick={() => {
  //   setSelectedDriverId(null);
  //   setSearchId("");
  //   setSearchNumber("");
  //   setSelectedLocKey(null);
  //   setSelectedOrderId(null);
  //   setHighlightLocKey(null);
  //   window

  // }}
  onClick={() => window.location.reload()}
/>
        <div style={{ display: "flex", alignItems: "center", marginLeft: 20 }}>

<Button
  label={String(totals.totalOrders)}
  tooltip="Total Orders"
  tooltipOptions={{ position: "top" }}
  onClick={() => handleTagClick("total")}
  style={{ backgroundColor: "#6b7280" }}
/>

  <img
    src={pinSvgUrl("#22c55e", totals.totalDelivered)}
    alt="Delivered"
    data-pr-tooltip={`Delivered: ${totals.totalDelivered}`}
    style={{ cursor: "pointer" }}
    onClick={() => handleTagClick("delivered")}
  />

  <img
    src={pinSvgUrl("#3b82f6", totals.totalPending)}
    alt={historical ? "Not delivered" : "Yet to Deliver"}
    data-pr-tooltip={`${historical ? "Delivery time out / pending outcomes" : "Yet to deliver / delivery time out"}: ${totals.totalPending}`}
    style={{ cursor: "pointer" }}
    onClick={() => handleTagClick("pending")}
  />

  <img
    src={pinSvgUrl("#f59e0b", totals.totaldelayed)}
    alt="Delayed orders"
    data-pr-tooltip={`Delayed orders: ${totals.totaldelayed}`}
    style={{ cursor: "pointer" }}
    onClick={() => handleTagClick("delayed")}
  />

  <img
    src={pinSvgUrl("#ef4444", totals.totalCancelled)}
    alt="Cancelled"
    data-pr-tooltip={`Cancelled: ${totals.totalCancelled}`}
    style={{ cursor: "pointer" }}
    onClick={() => handleTagClick("cancelled")}
  />

  {totals.totalUnknown > 0 && <Button label={`Unknown: ${totals.totalUnknown}`} severity="secondary" outlined
    onClick={() => handleTagClick("unknown")} />}

  <Tooltip target="img" position="top" />
</div>
      </div>

      {loading && <p role="status">Loading delivery assignments…</p>}
      {loadError && <p role="alert">{loadError}</p>}
      <DriverSummaryPanel driversSummary={driversSummary} selectedDriverId={selectedDriverId}
        historical={historical} handleDriverOrdersClick={handleDriverOrdersClick} />

      <OrdersDialog
        visible={showCancelledDialog}
        orders={cancelledOrders}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        title={dialogTitle}
        onHide={() => setShowCancelledDialog(false)}
      />

      {/* Google Map */}
      <GoogleMap
        center={mapCenter}
        zoom={12}
        onLoad={(m) => void (mapRef.current = m)}
        mapContainerStyle={{ width: "100%", height: "580px" }}
      >
        <OrdersLayer
          markers={markers}
          groups={groups}
          selectedLocKey={selectedLocKey}
          setSelectedLocKey={setSelectedLocKey}
          selectedOrderId={selectedOrderId}
          setSelectedOrderId={setSelectedOrderId}
          mapRef={mapRef}
          mapCenter={mapCenter}
          setMapCenter={setMapCenter}
          highlightLocKeys={highlightLocKeys}
          selectedDriverId={selectedDriverId}

        />
      </GoogleMap>
    </div>
  );
}
